// src/beta/components/slides/TextSlide.tsx
import type { V1Slide } from '@/beta/types/story';

interface Props { slide: V1Slide; }

/** Text-only slide — heading + body on a dark textured background.
 *  Handles both raw `text` blocks (heading/bodyText on block) and
 *  `orphan-text` slides (heading/bodyText on overlayTexts[0]).
 *  Optional blurred backgroundUrl carries forward from the previous visual. */
export function TextSlide({ slide }: Props) {
  const overlay = slide.overlayTexts[0];
  const heading = overlay?.heading ?? slide.block.heading ?? undefined;
  const body = overlay?.bodyText ?? slide.block.bodyText ?? undefined;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', background: 'var(--vm-dark)' }}>
      {slide.backgroundUrl && (
        <div
          data-testid="text-slide-bg"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${slide.backgroundUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(40px) brightness(0.35)',
            transform: 'scale(1.1)',
          }}
        />
      )}

      <div
        className="vm-fade-up-blur"
        style={{
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: 'var(--vm-s-12)',
          maxWidth: 880,
          margin: '0 auto',
          color: 'var(--vm-warm-white)',
        }}
      >
        {heading && (
          <h2
            style={{
              fontFamily: 'var(--vm-font-heading)',
              fontSize: 56,
              letterSpacing: '0.04em',
              lineHeight: 1.1,
              margin: 0,
            }}
          >
            {heading}
          </h2>
        )}
        {body && (
          <p
            style={{
              fontFamily: 'var(--vm-font-body)',
              fontSize: 22,
              lineHeight: 1.55,
              marginTop: heading ? 'var(--vm-s-3)' : 0,
              color: 'var(--vm-stone-light)',
            }}
          >
            {body}
          </p>
        )}
      </div>
    </div>
  );
}
